import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { login, setCompanyData, setCompanyPlanId, setSelectedCompany } from './reducers/user.reducers';
import rootReducer from './reducers/';
import { UserPermission } from 'constants/user.constants';
//services
import {
	getCountriesAsync,
	getCurrencyAsync,
	setContactLoginTime,
	getBankListAsync,
	getCurrencyPairs,
	getPlansAsync,
	getCountriesIncludeForbiddenAsync,
	setAppVersion,
	getPaymentPurpose,
} from 'services/global.services';
import { getBalancesByCompanyId } from 'services/balance.services';
import { getLangAsync } from 'state/reducers/translation.reducers';
import { upsertPayBeneficiary } from './reducers/pay.reducers';
import { getFeesByCompanyId } from 'services/pay.services';
import { setActiveDates, setNavbarSearchActiveDates } from './reducers/activity.reducers';
import { getActivitiesAsync, getBalancesActivityByCompanyId } from 'services/activity.services';
import { loadBeneficiaries } from 'services/beneficiary.services';
import { getNotificationsByCompanyId } from 'services/notifications.service';
import { togglePage } from './reducers/ui.reducers';
import _ from 'lodash';
import { pagesEnum } from 'constants/ui.constants';
import { beneficiaryIntialState } from 'constants/beneficiary.constants';
import { intializeBeneficiary } from './reducers/beneficiary.reducers';
import { setCompanyBalances } from './reducers/balance.reducers';
import { initialBalance } from 'constants/balance.constants';
import { getContactCompanies, getUserApprovals } from 'services/user.services';
import { fetchPendingSystemMessages } from 'services/system-messages.services';
import { getOpenPendingRequestsByCompanyId } from 'services/requests.services';

const INTERACTIVE_LOGIN_COMPLETED_KEY = 'INTERACTIVE_LOGIN_COMPLETED';

// Create the middleware instance and methods
const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
	actionCreator: login,
	effect: async (action, listenerApi) => {
		const { user, translation } = listenerApi.getState() as RootState;
		const isInteractiveLogin =
			typeof window !== 'undefined' && sessionStorage.getItem(INTERACTIVE_LOGIN_COMPLETED_KEY) === '1';
		if (typeof window !== 'undefined') {
			sessionStorage.removeItem(INTERACTIVE_LOGIN_COMPLETED_KEY);
		}

		if (user.userPermission !== UserPermission.NONE) {
			const langPromise = listenerApi
				.dispatch(getLangAsync(translation.language))
				.unwrap()
				.catch(() => null);

			if (isInteractiveLogin) {
				const loginTimePromise = listenerApi.dispatch(setContactLoginTime());
				loginTimePromise.finally(() => {
					listenerApi.dispatch(getContactCompanies());
				});

				Promise.all([langPromise, loginTimePromise]).finally(() => {
					listenerApi.dispatch(fetchPendingSystemMessages());
				});
			} else {
				const companiesPromise = listenerApi.dispatch(getContactCompanies());
				companiesPromise.finally(() => {
					listenerApi.dispatch(getOpenPendingRequestsByCompanyId());
				});
			}

			listenerApi.dispatch(getCountriesAsync());
			listenerApi.dispatch(getCurrencyAsync());
			listenerApi.dispatch(getUserApprovals());
			listenerApi.dispatch(getBankListAsync());
			listenerApi.dispatch(getPlansAsync());
			listenerApi.dispatch(getCountriesIncludeForbiddenAsync());
			listenerApi.dispatch(setAppVersion());
			listenerApi.dispatch(getPaymentPurpose());
		}
	},
});

listenerMiddleware.startListening({
	actionCreator: setNavbarSearchActiveDates,
	effect: async (action, listenerApi) => {
		const { user } = listenerApi.getState() as RootState;
		if (user?.details?.companyId) {
			listenerApi.dispatch(
				getActivitiesAsync(user.details.companyId, action.payload.startDate, action.payload.endDate)
			);
		}
	},
});

listenerMiddleware.startListening({
	actionCreator: togglePage,
	effect: async (action, listenerApi) => {
		if (_.isEqual(action.payload, `/${pagesEnum.BENEFICIARY}/new`)) {
			listenerApi.dispatch(intializeBeneficiary(beneficiaryIntialState));
		}
	},
});

listenerMiddleware.startListening({
	actionCreator: setCompanyData,
	effect: async (action, listenerApi) => {
		const { user } = listenerApi.getState() as RootState;
		listenerApi.dispatch(setCompanyBalances(initialBalance));
		listenerApi.dispatch(getBalancesByCompanyId());
		listenerApi.dispatch(loadBeneficiaries());
		listenerApi.dispatch(getNotificationsByCompanyId(action.payload.id));
		listenerApi.dispatch(getBalancesActivityByCompanyId(action.payload.id));
		// if(!_.get(user,'details.plan.isDefault')){
		listenerApi.dispatch(getCurrencyPairs());
		// }
	},
});

listenerMiddleware.startListening({
	actionCreator: setCompanyPlanId,
	effect: async (action, listenerApi) => {
		const { user } = listenerApi.getState() as RootState;
		if (!user?.details?.plan) {
			listenerApi.dispatch(togglePage(pagesEnum.DASHBOARD));
		}
	},
});

export const store = configureStore({
	reducer: rootReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({ serializableCheck: false }).prepend(listenerMiddleware.middleware),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
